import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Minus, Plus, Search, X } from 'lucide-react';
import { formatIDR } from '../../lib/format';
import { FotoMini } from '../FotoMini';
import { MoneyInput } from '../MoneyInput';
import { HapusCari } from '../HapusCari';
import { aksesorisUntuk } from '../../lib/aksesoris';

const inputCls =
  'w-full rounded-xl border border-input bg-ivory/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brown/30 transition-shadow';
const labelCls = 'text-xs font-semibold uppercase tracking-wider text-mocha';


export function Stepper({ value, max, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(0, value - 1))}
        className="w-7 h-7 rounded-lg border border-border bg-white text-brown flex items-center justify-center disabled:opacity-30" disabled={value <= 0}>
        <Minus size={13} />
      </button>
      <span className="w-6 text-center text-sm font-bold text-ink">{value}</span>
      <button type="button" data-testid={`${testid}-plus`} onClick={() => onChange(Math.min(max, value + 1))}
        className="w-7 h-7 rounded-lg border border-border bg-white text-brown flex items-center justify-center disabled:opacity-30" disabled={value >= max}>
        <Plus size={13} />
      </button>
    </div>
  );
}

export function ProductSearch({ products, onPick, testid, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const matches = useMemo(
    () =>
      query
        ? products.filter(
            (p) =>
              p.name.toLowerCase().includes(query.toLowerCase()) ||
              p.code.toLowerCase().includes(query.toLowerCase())
          )
        : [],
    [products, query]
  );
  return (
    <div className="relative">
      <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mocha" />
      <input
        data-testid={testid}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder}
        className={`${inputCls} pl-10 pr-9`}
      />
      <HapusCari show={!!query} onClear={() => { setQuery(''); setOpen(false); }} testid={`${testid}-clear`} />
      {open && query && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-border rounded-xl shadow-lift max-h-56 overflow-y-auto" data-testid={`${testid}-suggestions`}>
          {matches.length === 0 && <p className="px-4 py-3 text-xs text-mocha">Tidak ada produk cocok.</p>}
          {matches.slice(0, 12).map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`${testid}-option-${p.code}`}
              onMouseDown={() => {
                onPick(p);
                setQuery('');
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-ivory flex items-center justify-between gap-2.5"
            >
              <FotoMini product={p} size={32} testid={`${testid}-foto-${p.code}`} />
              <span className="flex-1 min-w-0">
                <b className="text-brown">{p.code}</b> {p.name}
                <span className="block text-[11px] text-mocha">{p.category} • {formatIDR(p.rental_price)}/pcs</span>
              </span>
              <span className={`text-[10px] font-bold whitespace-nowrap ${p.available_total > 0 ? 'text-success' : 'text-danger'}`}>
                ready {p.available_total}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function BarangRow({ index, row, availability, onChange, onRemove }) {
  const product = availability.find((p) => p.id === row.product_id);
  const bawahanDefault = product?.default_bawahan_id
    ? availability.find((p) => p.id === product.default_bawahan_id)
    : null;
  const bawahanProduct = row.bawahan_custom
    ? availability.find((p) => p.id === row.bawahan_product_id)
    : bawahanDefault;

  const set = (patch) => onChange({ ...row, ...patch });
  const qty = Object.values(row.sizes).reduce((a, b) => a + b, 0);

  const pickProduct = (p) =>
    set({
      product_id: p.id,
      sizes: {},
      price_per_pcs: p.rental_price,
      bawahan_product_id: null,
      bawahan_sizes: {},
      bawahan_custom: false,
      // Barang baru: aksesoris bawaan menyala otomatis (bisa dimatikan).
      aksesoris: aksesorisUntuk(p.category),
    });

  // Aksesoris bawaan set (jilbab, kalung, dst). Hanya daftar untuk dicek saat barang
  // kembali: tidak mengubah harga, jumlah pcs, atau stok. Bawahan tetap lewat kotak
  // Bawahan di atas.
  const aksDaftar = aksesorisUntuk(product?.category);
  const aksDipilih = (row.aksesoris || []).filter((n) => aksDaftar.includes(n));
  const aksOn = aksDipilih.length > 0;
  const aksToggle = () => set({ aksesoris: aksOn ? [] : aksDaftar });
  const aksChip = (nama) =>
    set({
      aksesoris: aksDaftar.filter((n) => (n === nama ? !aksDipilih.includes(n) : aksDipilih.includes(n))),
    });

  const sizeKeys = product ? Object.keys(product.sizes).filter((s) => product.sizes[s] > 0) : [];
  const availOf = (s) => product?.available_sizes?.[s] ?? 0;

  const takeAll = () => {
    const sizes = {};
    sizeKeys.forEach((s) => {
      if (availOf(s) > 0) sizes[s] = availOf(s);
    });
    set({ sizes });
  };

  const [splitN, setSplitN] = useState('');
  const autoSplit = () => {
    let n = parseInt(splitN, 10);
    if (!n || n <= 0) return toast.error('Isi jumlah yang mau dibagi');
    const sizes = {};
    const sorted = [...sizeKeys].sort((a, b) => availOf(b) - availOf(a));
    for (const s of sorted) {
      if (n <= 0) break;
      const take = Math.min(availOf(s), n);
      if (take > 0) sizes[s] = take;
      n -= take;
    }
    if (n > 0) toast.error(`Stok kurang: ${n} pcs tidak terpenuhi`);
    set({ sizes });
  };

  const bwSizes = row.bawahan_sizes || {};
  const bwSizeKeys = bawahanProduct ? Object.keys(bawahanProduct.sizes).filter((s) => bawahanProduct.sizes[s] > 0) : [];
  const bwAvailOf = (s) => bawahanProduct?.available_sizes?.[s] ?? 0;
  const bawahanChoices = availability.filter((p) => p.category === 'Bawahan');

  return (
    <div className="bg-white rounded-2xl border border-border shadow-soft p-5 space-y-4" data-testid={`barang-row-${index}`}>
      <div className="flex items-center justify-between">
        <p className="font-display text-lg text-brown-dark">Barang {index + 1}</p>
        <button type="button" data-testid={`barang-remove-${index}`} onClick={onRemove} className="p-1.5 rounded-lg text-danger hover:bg-danger/10">
          <X size={16} />
        </button>
      </div>

      {!product ? (
        <ProductSearch
          products={availability}
          testid={`barang-search-${index}`}
          placeholder="Ketik nama barang… contoh: kebaya, bodo, songket"
          onPick={pickProduct}
        />
      ) : (
        <div className="rounded-xl bg-ivory border border-border p-4 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-3 min-w-0">
              <FotoMini product={product} size={46} testid={`barang-foto-${index}`} />
              <div className="min-w-0">
                <p className="text-sm font-bold text-ink">
                  <span className="text-brown">{product.code}</span> {product.name}
                </p>
                <p className="text-xs text-mocha mt-0.5">
                  {product.category}
                  {product.set_contents ? ` • Isi set: ${product.set_contents}` : ''}
                </p>
              </div>
            </div>
            <button type="button" data-testid={`barang-change-${index}`} onClick={() => set({ product_id: null, sizes: {} })} className="text-xs font-semibold text-brown hover:underline whitespace-nowrap">
              Ganti
            </button>
          </div>

          {(bawahanDefault || row.bawahan_custom) && (
            <div className="rounded-xl border border-gold/30 bg-gold/5 p-3.5 space-y-3" data-testid={`bawahan-box-${index}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs font-bold uppercase tracking-wider text-brown-med">Bawahan</p>
                <button
                  type="button"
                  data-testid={`bawahan-custom-toggle-${index}`}
                  onClick={() =>
                    set({
                      bawahan_custom: !row.bawahan_custom,
                      bawahan_product_id: !row.bawahan_custom ? bawahanDefault?.id || null : null,
                      bawahan_sizes: {},
                    })
                  }
                  className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold transition-colors ${
                    row.bawahan_custom ? 'bg-caramel text-white' : 'bg-white border border-border text-mocha'
                  }`}
                >
                  {row.bawahan_custom ? 'Custom aktif' : 'Standar — ubah?'}
                </button>
              </div>
              {!row.bawahan_custom ? (
                <>
                  <p className="text-sm">
                    <span className="rounded-full bg-brown/10 text-brown text-[10px] font-bold px-2.5 py-0.5 mr-2">STANDAR</span>
                    <b>{bawahanDefault?.name || product.default_bawahan_name}</b>
                    <span className="block text-[11px] text-mocha mt-1">Ukuran bawahan otomatis mengikuti skema ukuran bawahan (misal ALL SIZE/STANDAR).</span>
                  </p>
                  {bawahanDefault && qty > 0 && (bawahanDefault.available_total ?? 0) < qty && (
                    <p className="text-[11px] font-bold text-danger bg-danger/10 rounded-lg px-3 py-2" data-testid={`bawahan-warning-${index}`}>
                      Stok bawahan standar tidak cukup untuk {qty} pcs pada tanggal ini — aktifkan Custom untuk memilih bawahan lain.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <ProductSearch
                    products={bawahanChoices}
                    testid={`bawahan-search-${index}`}
                    placeholder="Cari bawahan (kategori Bawahan saja)…"
                    onPick={(p) => set({ bawahan_product_id: p.id, bawahan_sizes: {} })}
                  />
                  {bawahanProduct && row.bawahan_product_id === bawahanProduct.id && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">
                        <span className="text-brown">{bawahanProduct.code}</span> {bawahanProduct.name}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {bwSizeKeys.map((s) => (
                          <div key={s} className="rounded-xl border border-border bg-white px-3 py-2 text-center">
                            <p className="text-[10px] font-bold text-mocha">{s}</p>
                            <p className="text-[10px] text-mocha mb-1">stok {bwAvailOf(s)}</p>
                            <Stepper
                              testid={`bawahan-size-${index}-${s.replace(/[^A-Z0-9]/gi, '')}`}
                              value={bwSizes[s] || 0}
                              max={bwAvailOf(s)}
                              onChange={(v) => set({ bawahan_sizes: { ...bwSizes, [s]: v } })}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-mocha">Kosongkan semua = otomatis mengikuti skema ukuran bawahan.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-brown-med">Ukuran & Jumlah</p>
              <button type="button" data-testid={`take-all-${index}`} onClick={takeAll} className="rounded-full bg-brown/10 text-brown px-3 py-1 text-[11px] font-bold hover:bg-brown/20">
                Ambil semua stok
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {sizeKeys.map((s) => (
                <div
                  key={s}
                  data-testid={`size-box-${index}-${s.replace(/[^A-Z0-9]/gi, '')}`}
                  className={`rounded-xl border px-3 py-2 text-center transition-colors ${
                    (row.sizes[s] || 0) > 0 ? 'border-brown bg-brown/5' : 'border-border bg-white'
                  } ${availOf(s) === 0 ? 'opacity-40' : ''}`}
                >
                  <p className="text-[10px] font-bold text-mocha">{s}</p>
                  <p className="text-[10px] text-mocha mb-1">stok {availOf(s)}</p>
                  <Stepper
                    testid={`size-${index}-${s.replace(/[^A-Z0-9]/gi, '')}`}
                    value={row.sizes[s] || 0}
                    max={availOf(s)}
                    onChange={(v) => set({ sizes: { ...row.sizes, [s]: v } })}
                  />
                </div>
              ))}
              {sizeKeys.length === 0 && <p className="text-xs text-danger">Produk ini belum punya data stok ukuran.</p>}
            </div>
            <div className="flex items-center gap-2 mt-2.5 flex-wrap">
              <span className="text-[11px] text-mocha">atau ketik jumlahnya:</span>
              <input
                data-testid={`split-input-${index}`}
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                min="1"
                value={splitN}
                onChange={(e) => setSplitN(e.target.value)}
                className="w-20 rounded-lg border border-input bg-white px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brown/30"
              />
              <button type="button" data-testid={`auto-split-${index}`} onClick={autoSplit} className="rounded-full bg-gold/20 text-gold-dark px-3 py-1.5 text-[11px] font-bold hover:bg-gold/30">
                Bagi otomatis
              </button>
            </div>
          </div>

          {aksDaftar.length > 0 && (
            <div className="rounded-xl border border-border bg-white p-3.5" data-testid={`aksesoris-box-${index}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wider text-brown-med">Aksesoris bawaan</p>
                  <p className="text-[11px] text-mocha mt-0.5">
                    Jumlah mengikuti jumlah setelan. Harga, pcs, dan stok tidak berubah.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={aksOn}
                  aria-label="Pakai aksesoris bawaan"
                  data-testid={`aksesoris-toggle-${index}`}
                  onClick={aksToggle}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${aksOn ? 'bg-success' : 'bg-mocha/30'}`}
                >
                  <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${aksOn ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
              {aksOn && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {aksDaftar.map((nama) => {
                    const ada = aksDipilih.includes(nama);
                    return (
                      <button
                        key={nama}
                        type="button"
                        data-testid={`aksesoris-chip-${index}-${nama.replace(/\s+/g, '-')}`}
                        onClick={() => aksChip(nama)}
                        title={ada ? 'Klik kalau tidak dibawa' : 'Klik untuk dibawa lagi'}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          ada ? 'bg-brown/10 text-brown border-brown/20' : 'bg-white text-mocha border-border line-through opacity-60'
                        }`}
                      >
                        {nama}{qty > 0 ? ` ×${qty}` : ''}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-end justify-between gap-3 flex-wrap border-t border-dashed border-border pt-3">
            <div>
              <label className={labelCls}>Harga /pcs</label>
              <MoneyInput
                data-testid={`price-input-${index}`}
                value={row.price_per_pcs ?? ''}
                onChange={(v) => set({ price_per_pcs: Number(v) || 0 })}
                className="mt-1 w-32 rounded-lg border border-input bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brown/30"
              />
            </div>
            <p className="text-sm">
              <b data-testid={`row-qty-${index}`}>{qty} pcs</b> × {formatIDR(row.price_per_pcs || 0)} ={' '}
              <b className="text-brown" data-testid={`row-subtotal-${index}`}>{formatIDR(qty * (row.price_per_pcs || 0))}</b>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
